import { isGone, normalizeOperationOutcome } from '@medplum/core'
import type { Reference } from '@medplum/fhirtypes'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
  ResourceOf,
} from '@seen/core/ExtendedResource'
import { extractId } from '@seen/core/utils'
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export interface UseReadOptions<T> {
  /* Suppress the error toast for a read the user did not initiate, where a
   * failure is meant to drop what it feeds rather than be reported. The
   * console.error is still emitted. */
  silentErrors?: boolean
  query?: Omit<
    UseQueryOptions<
      T,
      unknown,
      T,
      ReturnType<(typeof cacheKeys)['readExtended']>
    >,
    'initialData' | 'queryKey' | 'queryFn'
  >
}

export function useRead<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T> | null | undefined,
  id?:
    | Reference<NoInfer<ResourceOf<T>>>
    | string
    | NoInfer<ResourceOf<T>>
    | null,
  options?: UseReadOptions<T>
): UseQueryResult<T> {
  const medplum = useMedplum()
  const resolvedId = extractId(id) ?? ''
  const type = resourceClass?.RESOURCE_TYPE
  const className = resourceClass?.name

  const enabled =
    Boolean(type) &&
    Boolean(resolvedId) &&
    Boolean(className) &&
    Boolean(resourceClass) &&
    (options?.query?.enabled === undefined || options?.query?.enabled)
  const result = useQuery<T>({
    ...(options?.query as any),
    enabled,
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: className
      ? cacheKeys.readExtended(type, resolvedId, className)
      : undefined,
    queryFn: resourceClass
      ? async () => medplum.readResourceClass(resourceClass, resolvedId)
      : undefined,
    meta: {
      ...options?.query?.meta,
      queryParams: {
        _id: resolvedId,
      },
      resourceClass,
    },
  })

  // 410 Gone = resource deleted. Inbox feeds and other stale-reference sources
  // commonly race with deletions; surfacing these as toasts creates noise and
  // intercepts pointer events in e2e tests. Keep console.error for diagnostics.
  const isDeleted =
    !!result.error && isGone(normalizeOperationOutcome(result.error))

  useErrorToast(
    result.error &&
      enabled &&
      `Error accessing ${type}/${resolvedId}: ${result.error.message ?? 'Unknown error'}`,
    { silent: isDeleted || options?.silentErrors }
  )

  return result
}
